import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';

const PublicStrategicPlanPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [plan, setPlan] = useState<any>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`/api/public/strategic-plan/${token}`)
      .then(r => { setPlan(r.data); setLoading(false); })
      .catch(() => { setError('התכנית לא נמצאה או אינה ציבורית'); setLoading(false); });
  }, [token]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
    </div>
  );
  if (error) return (
    <div className="min-h-screen flex items-center justify-center text-gray-500">{error}</div>
  );
  if (!plan) return null;

  const d = plan.data;

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4" dir="rtl">
      <div className="max-w-4xl mx-auto">

        {/* Header */}
        <div className="bg-gradient-to-l from-blue-700 to-indigo-800 text-white rounded-2xl p-8 mb-8 shadow-xl">
          <div className="flex items-center gap-3 mb-4">
            <img src="/amutech-logo.png" alt="Amutech" className="h-10 opacity-80" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          </div>
          <h1 className="text-3xl font-black mb-2">תכנית אסטרטגית ותכנית עבודה</h1>
          {d.vision && <p className="text-blue-100 text-lg mt-2">{d.vision}</p>}
        </div>

        {/* Vision & Mission */}
        {(d.vision || d.mission) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {d.vision && (
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h2 className="text-lg font-bold text-blue-800 mb-3">🎯 חזון</h2>
                <p className="text-gray-700 leading-relaxed">{d.vision}</p>
              </div>
            )}
            {d.mission && (
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h2 className="text-lg font-bold text-indigo-800 mb-3">🚀 ייעוד</h2>
                <p className="text-gray-700 leading-relaxed">{d.mission}</p>
              </div>
            )}
          </div>
        )}

        {/* Values */}
        {d.values && d.values.length > 0 && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-8">
            <h2 className="text-lg font-bold text-gray-800 mb-4">✨ ערכי הארגון</h2>
            <div className="flex flex-wrap gap-2">
              {d.values.map((v: string, i: number) => (
                <span key={i} className="bg-indigo-50 text-indigo-700 border border-indigo-200 px-3 py-1.5 rounded-full text-sm font-medium">
                  {v}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Target Audience */}
        {d.targetAudience && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-8">
            <h2 className="text-lg font-bold text-gray-800 mb-3">👥 קהל היעד</h2>
            <p className="text-gray-700 leading-relaxed">{d.targetAudience}</p>
          </div>
        )}

        {/* SWOT */}
        {(d.swot?.strengths || d.swot?.weaknesses || d.swot?.opportunities || d.swot?.threats) && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-8">
            <h2 className="text-lg font-bold text-gray-800 mb-4">📊 ניתוח SWOT</h2>
            <div className="grid grid-cols-2 gap-4">
              {d.swot.strengths && (
                <div className="bg-green-50 rounded-xl p-4 border border-green-100">
                  <h3 className="font-bold text-green-700 mb-2">💪 חוזקות</h3>
                  <p className="text-sm text-gray-700 whitespace-pre-line">{d.swot.strengths}</p>
                </div>
              )}
              {d.swot.weaknesses && (
                <div className="bg-red-50 rounded-xl p-4 border border-red-100">
                  <h3 className="font-bold text-red-700 mb-2">⚠️ חולשות</h3>
                  <p className="text-sm text-gray-700 whitespace-pre-line">{d.swot.weaknesses}</p>
                </div>
              )}
              {d.swot.opportunities && (
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                  <h3 className="font-bold text-blue-700 mb-2">🌟 הזדמנויות</h3>
                  <p className="text-sm text-gray-700 whitespace-pre-line">{d.swot.opportunities}</p>
                </div>
              )}
              {d.swot.threats && (
                <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
                  <h3 className="font-bold text-amber-700 mb-2">🔴 איומים</h3>
                  <p className="text-sm text-gray-700 whitespace-pre-line">{d.swot.threats}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Goals */}
        {d.goals?.filter((g: any) => g.title)?.length > 0 && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-8">
            <h2 className="text-lg font-bold text-gray-800 mb-4">🎯 יעדים אסטרטגיים</h2>
            <div className="space-y-3">
              {d.goals.filter((g: any) => g.title).map((goal: any, i: number) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                  <div className="w-8 h-8 rounded-full bg-blue-600 text-white text-sm font-bold flex items-center justify-center flex-shrink-0">
                    {i + 1}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-800">{goal.title}</div>
                    {goal.description && <div className="text-sm text-gray-600 mt-1">{goal.description}</div>}
                    <div className="flex gap-2 mt-1">
                      {goal.timeline && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{goal.timeline}</span>}
                      {goal.priority && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">עדיפות: {goal.priority}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Work Plan */}
        {d.workPlan?.filter((w: any) => w.action)?.length > 0 && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-8">
            <h2 className="text-lg font-bold text-gray-800 mb-4">📋 תכנית עבודה</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-right p-2 font-semibold text-gray-600">פעולה</th>
                    <th className="p-2 font-semibold text-gray-600">אחראי</th>
                    <th className="p-2 font-semibold text-gray-600">Q1</th>
                    <th className="p-2 font-semibold text-gray-600">Q2</th>
                    <th className="p-2 font-semibold text-gray-600">Q3</th>
                    <th className="p-2 font-semibold text-gray-600">Q4</th>
                    <th className="p-2 font-semibold text-gray-600">סטטוס</th>
                  </tr>
                </thead>
                <tbody>
                  {d.workPlan.filter((w: any) => w.action).map((row: any, i: number) => (
                    <tr key={i} className="border-t border-gray-100">
                      <td className="p-2 text-right">{row.action}</td>
                      <td className="p-2 text-center text-gray-600">{row.responsible}</td>
                      <td className="p-2 text-center">{row.q1 ? '✓' : ''}</td>
                      <td className="p-2 text-center">{row.q2 ? '✓' : ''}</td>
                      <td className="p-2 text-center">{row.q3 ? '✓' : ''}</td>
                      <td className="p-2 text-center">{row.q4 ? '✓' : ''}</td>
                      <td className="p-2 text-center">
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          row.status === 'הושלם' ? 'bg-green-100 text-green-700' :
                          row.status === 'בביצוע' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {row.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Budget */}
        {d.budget?.filter((b: any) => b.area)?.length > 0 && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-8">
            <h2 className="text-lg font-bold text-gray-800 mb-4">💰 תקציב</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-right p-2 font-semibold text-gray-600">תחום</th>
                    <th className="p-2 font-semibold text-gray-600">מתוכנן (₪)</th>
                    <th className="p-2 font-semibold text-gray-600">בפועל (₪)</th>
                  </tr>
                </thead>
                <tbody>
                  {d.budget.filter((b: any) => b.area).map((row: any, i: number) => (
                    <tr key={i} className="border-t border-gray-100">
                      <td className="p-2 text-right">{row.area}</td>
                      <td className="p-2 text-center text-gray-700">₪{Number(row.planned || 0).toLocaleString()}</td>
                      <td className="p-2 text-center text-gray-700">₪{Number(row.actual || 0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-sm text-gray-400 py-4">
          נוצר באמצעות <strong>Amutech</strong> — מערכת ניהול עמותות
        </div>
      </div>
    </div>
  );
};

export default PublicStrategicPlanPage;
